import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-accordeon.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DoesNotExistAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { examTypeBadge } from '@ils/examType/client/examTypeBadge.js';
import { promptForExamType } from '@ils/examType/client/selectExamTypePrompt.js';
import { filterBadgeBar } from '@ils/filter-badges/client/filter-badge-bar.js';
import { subscribeOSCEGroupsByFilter } from '@ils/osces/api/osce-group.js';
import { OSCEGroup } from '@ils/osces/db/osce-group.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { OsceCategories, OSCECategory, prettyOsceCategory } from '@ils/static/enums.js';
import { DEFAULT_VOICE_DURATION } from '@ils/static/values.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { copyOsceByID, subscribeOscesByFilter } from '../api/osce.js';
import { OSCE } from '../db/osce.js';
import { OSCEText } from '../db/osceText.js';

const NEW_OSCES = 'Neue OSCEs';

const tabs = ['OSCEs', 'OSCE-Gruppen'];

/**
 * @element ils-osce-overview
 */
@customElement('ils-osce-overview')
export class ILSOSCEOverview extends ChemistryLitElement {
  @property({ type: Boolean }) editable = false;

  @state() private readonly branding = new TranslationController(this);

  @state() private readonly _currentUser = new RXController(this, currentUser());

  @state() private _tab = tabs[0];

  @state() private _showArchivedOSCEs = false;

  private readonly view = new View<typeof OSCE>({
    filter: new AdornisFilter({
      search: '',
      searchFields: ['title', 'titleTrainee'],
      aspects: [
        {
          _class: 'StringEQUALSAspect',
          mode: 'equals',
          value: OSCECategory.FAMED_ANMANESEGESPRAECH,
          fieldName: 'osceType',
        },
        {
          _class: 'DoesNotExistAspect',
          fieldName: 'archivedAt',
        },
      ],
    }),
  });

  override render() {
    return html`
      <x-tabs .selected=${tabs.indexOf(this._tab)}>
        ${repeat(
          tabs,
          viewpoint => viewpoint,
          viewpoint => html` <x-tab @click=${() => (this._tab = viewpoint)}> ${viewpoint} </x-tab> `,
        )}
      </x-tabs>
      ${this._tab === tabs[0] ? this._osceList() : this._groupList()}
    `;
  }

  private _osceList() {
    const aspect = this.view.filter?.aspects.find(a => a.fieldName === 'osceType');
    const selected = aspect?._class === DoesNotExistAspect._class ? NEW_OSCES : aspect?.value ?? NEW_OSCES;
    const selectables: string[] = [...OsceCategories]; // copy so we dont modify the original array
    if (hasPermission(this._currentUser.value, 'OSCEs', 'viewnew')) selectables.push(NEW_OSCES);
    return html`
      <x-flex space="sm">
        <x-flex horizontal wrap space="sm" padding="sm" ${css({ paddingBottom: '0' })}>
          ${filterBadgeBar({
            selectables,
            renderString: value => (value === NEW_OSCES ? NEW_OSCES : prettyOsceCategory(value)),
            onSelect: value => this._filterByOsceType(value),
            selected,
          })}
          <div flex></div>

          <x-flex horizontal crossaxis-center space="md">
            <x-input
              flex
              clearable
              placeholder="Suchen..."
              placeholder-mode="hide"
              @value-changed=${(e: ValueEvent<string>) => {
                if (this.view.filter?.search !== e.detail.value) this.view.filter.search = e.detail.value;
              }}
            ></x-input>
            <x-toggle-button
              @click=${() => this._toggleFilterByArchived()}
              label="Archivierte OSCEs anzeigen"
              ?checked=${this._showArchivedOSCEs}
            ></x-toggle-button>
            ${this.editable
              ? permissionGuard({
                  resource: 'OSCEs',
                  action: 'create',
                  guardedTemplate: html`
                    <x-button mode="text" icon="add" @click=${() => this._createNewOSCE()}>
                      Neuen ${this.branding.translate('osce')} hinzufügen
                    </x-button>
                  `,
                })
              : nothing}
          </x-flex>
        </x-flex>
        ${this._osceDataView()}
      </x-flex>
    `;
  }
  private _toggleFilterByArchived() {
    this._showArchivedOSCEs = !this._showArchivedOSCEs;

    if (!this.view.filter) return;
    const aspectIndex = this.view.filter.aspects.findIndex(a => a.fieldName === 'archivedAt');

    // we want to show -> remove the filter if it exists
    if (this._showArchivedOSCEs && aspectIndex !== -1) {
      this.view.filter.aspects.splice(aspectIndex, 1);
    }
    // we want to hide -> add the filter if it doesn't exist
    else if (!this._showArchivedOSCEs && aspectIndex === -1) {
      this.view.filter.aspects.push(new DoesNotExistAspect({ fieldName: 'archivedAt' }));
    }
    this.requestUpdate();
  }

  private _osceDataView() {
    return html`
      <x-data-view
        .options=${{
          class: OSCE,
          operation: subscribeOscesByFilter,
          isEntryClickable: true,

          loader: () =>
            html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center> <ils-loader></ils-loader> </x-flex>`,
          emptyState: () =>
            html`
              <x-empty-placeholder
                .text=${`Keine ${this.branding.translate(
                  'osces',
                )} gefunden. Möglicherweise hat der Administrator Sie noch nicht auf die relevanten Inhalte freigeschaltet oder Sie haben in nächster Zeit kein Ereignis.`}
              ></x-empty-placeholder>
            `,
          customColumns: [
            {
              fieldName: 'title',
              renderItem: osce => html`
                <x-flex horizontal crossaxis-center flex>
                  <ils-osce-type .osce="${osce}" ${css({ width: '30px' })}></ils-osce-type>
                  <x-text flex> ${osce.title} </x-text>
                </x-flex>
              `,
              selectionSet: {
                title: 1,
                osceType: 1,
                taskType: 1,
                enableVideo: 1,
                conversationType: 1,
              },
            },
            {
              fieldName: 'archivedAt',
              columnName: '',
              columnWidth: 'max-content',
              renderItem: osce =>
                osce.archivedAt
                  ? html`
                      <x-flex>
                        <x-icon> lock </x-icon>
                        <x-tooltip>
                          <x-text>
                            Archiviert am ${DateTime.fromJSDate(osce.archivedAt).toLocaleString(DateTime.DATE_SHORT)}
                          </x-text>
                        </x-tooltip>
                      </x-flex>
                    `
                  : nothing,
            },
            { fieldName: 'examType', columnWidth: 'max-content', renderItem: osce => examTypeBadge(osce.examType) },
            {
              customID: '_options',
              columnName: '',
              columnWidth: 'max-content',
              renderItem: (osce: OSCE) =>
                this.editable
                  ? html`
                      <x-flex horizontal space="sm">
                        ${permissionGuard({
                          resource: 'OSCEs',
                          action: 'create',
                          data: osce,
                          guardedTemplate: html`
                            <x-button
                              mode="text"
                              icon="content_copy"
                              @click=${(e: MouseEvent) => {
                                e.stopPropagation();
                                return XNativeDialog.guard(() => copyOsceByID(osce._id)());
                              }}
                            >
                              Kopieren
                            </x-button>
                          `,
                        })}
                        ${permissionGuard({
                          resource: 'OSCEs',
                          action: 'modify',
                          data: osce,
                          guardedTemplate: html`
                            <x-button
                              mode="text"
                              icon="edit"
                              @click=${(e: MouseEvent) => {
                                e.stopPropagation();
                                goTo({ path: `/start/editor-osce/${osce._id}`, searchParams: NO_CHANGE });
                              }}
                            >
                              Bearbeiten
                            </x-button>
                          `,
                        })}
                      </x-flex>
                    `
                  : nothing,
              selectionSet: { _id: 1 },
            },
          ],
          view: this.view,
        } satisfies DataViewOptions<typeof OSCE>}
        @entry-clicked=${(event: CustomEvent<{ column: ColumnDefinition<typeof OSCE>; entity: OSCE }>) => {
          goTo({ path: `/start/content-osce/${event.detail.entity._id}`, searchParams: NO_CHANGE });
        }}
      ></x-data-view>
    `;
  }

  private async _createNewOSCE() {
    const examType = await promptForExamType('Für welchen ExamType soll ein neues OSCE erstellt werden?');
    const osce = new OSCE({
      title: `Neuer ${this.branding.translate('osce') as string}`,
      examType,
      createdAt: new Date(),
      titleTrainee: '',
      startButtonDuration: 30,
      videoFileID: '',
      enableVideo: true,
      durationInMinutes: DEFAULT_VOICE_DURATION,
      instructionCategories: [],
      text: new OSCEText({
        shortDescription: '',
        sceneDescriptionTrainee: '',
        taskDescriptionTrainee: '',
        sceneDescriptionTester: '',
      }),
      files: [],
      taskType: 'voice',
      conversationType: 'DrDr',
    });

    await XNativeDialog.guard(() => osce.create(), {
      success: () => {
        void XSnackbar.show(
          `Ein neuer ${this.branding.translate('osce') as string} wurde erstellt unter 'Neue ${
            this.branding.translate('osces') as string
          } / Entwürfe'`,
        );
        this._filterByOsceType(NEW_OSCES);
      },
    });
  }

  private _filterByOsceType(osceType: string) {
    if (!this.view.filter) return;
    const aspect = this.view.filter.aspects.findIndex(a => a.fieldName === 'osceType');
    if (aspect === -1) return;
    if (osceType === NEW_OSCES) {
      this.view.filter.aspects[aspect] = new DoesNotExistAspect({
        fieldName: 'osceType',
      });
    } else {
      this.view.filter.aspects[aspect] = new StringAspect({
        fieldName: 'osceType',
        value: osceType,
        mode: 'equals',
      });
    }
    this.requestUpdate();
  }

  private _groupList() {
    return html`
      <x-flex space="sm">
        ${this.editable
          ? permissionGuard({
              resource: 'osceGroups',
              action: 'create',
              guardedTemplate: html`
                <x-button
                  mode="text"
                  icon="add"
                  ${css({ paddingTop: this.spacing.sm, paddingRight: this.spacing.sm })}
                  @click=${() => this._createNewOSCEGroup()}
                  ${css({ alignSelf: 'end' })}
                >
                  Neue OSCE-Gruppe hinzufügen
                </x-button>
              `,
            })
          : nothing}
        ${this._groupDataView()}
      </x-flex>
    `;
  }

  private _groupDataView() {
    return html`
      <x-data-view
        .options=${{
          class: OSCEGroup,
          operation: subscribeOSCEGroupsByFilter,
          loader: () =>
            html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center> <ils-loader></ils-loader> </x-flex>`,
          emptyState: () =>
            html` <x-empty-placeholder .text=${'Jetzt die erste OSCE-Gruppe erstellen'}></x-empty-placeholder> `,
          customColumns: [
            { fieldName: 'name' },
            { fieldName: 'examType', renderItem: group => examTypeBadge(group.examType) },
            {
              customID: '_options',
              columnName: '',
              columnWidth: 'max-content',
              renderItem: (entry: OSCEGroup) =>
                this.editable
                  ? html`
                      <x-flex horizontal space="sm">
                        ${permissionGuard({
                          resource: 'osceGroups',
                          action: 'modify',
                          data: entry,
                          guardedTemplate: html`
                            <x-button
                              mode="text"
                              icon="edit"
                              @click=${() =>
                                goTo({ path: `/start/editor-osce-group/${entry._id}`, searchParams: NO_CHANGE })}
                            >
                              Bearbeiten
                            </x-button>
                          `,
                        })}
                      </x-flex>
                    `
                  : nothing,
              selectionSet: { _id: 1 },
            },
          ],
          view: new View<typeof OSCEGroup>({
            filter: new AdornisFilter({}),
          }),
        } satisfies DataViewOptions<typeof OSCEGroup>}
      ></x-data-view>
    `;
  }

  private async _createNewOSCEGroup() {
    const examType = await promptForExamType('Für welchen ExamType soll eine neue OSCE-Gruppe erstellt werden?');
    await XNativeDialog.guard(() => new OSCEGroup({ name: 'Neue OSCE-Gruppe', examType }).create(), {
      success: () => {
        void XSnackbar.show('OSCE-Gruppe erfolgreich erstellt');
      },
    });
  }
}
